import React, { useState } from 'react';
import { ChevronDown, HelpCircle, MessageCircle, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const faqData = [
  {
    question: "¿Qué es exactamente el dispositivo Bravo y en qué se diferencia del satélite?",
    answer: "Bravo es un receptor IP de alta tecnología desarrollado para sustituir la compleja infraestructura del satélite. A diferencia de las antenas parabólicas, Bravo recibe la señal de alta definición (4K/HD) directamente a través de una conexión a internet estable. Esto elimina costes de instalación, problemas de mantenimiento por climatología y la necesidad de permisos para colocar antenas en azoteas."
  },
  {
    question: "¿Necesito un servidor de cine profesional o un proyeccionista DCI para usar Bravo?",
    answer: "No. Bravo ha sido diseñado para democratizar el acceso a la cultura. Se conecta mediante una salida HDMI o SDI estándar a cualquier proyector (ya sea de cine profesional o de auditorio). Su interfaz es tan intuitiva que puede ser gestionada por personal de sala sin conocimientos técnicos avanzados, controlando la proyección directamente desde un smartphone."
  },
  {
    question: "¿Cómo funciona la proyección de óperas o conciertos grabados si no hay DCP ni KDM?",
    answer: "Para el contenido que no es en directo, Bravo elimina la logística de los discos duros (DCP) y las claves de desbloqueo (KDM). El contenido se aloja de forma segura en la nube y el dispositivo lo reproduce vía streaming o descarga previa, garantizando una calidad cinematográfica sin el estrés de esperar un paquete físico o una llave digital."
  },
  {
    question: "¿Cuáles son los requisitos técnicos mínimos para mi sala?",
    answer: "Solo necesita tres elementos básicos:\n\n• Una conexión a internet de banda ancha estable (preferiblemente fibra óptica).\n• Un proyector con entrada HDMI o SDI.\n• Un sistema de sonido conectado a la sala (vía HDMI o salida auxiliar)."
  },
  {
    question: "¿Qué tipo de contenidos puedo programar a través de CinemaStreaming?",
    answer: "Ofrecemos acceso al catálogo de Versión Digital, líder en Event Cinema. Esto incluye la temporada exclusiva de la Royal Opera House de Londres, producciones del Teatro Real y el Liceu, conciertos de grandes estrellas (K-Pop, Rock, Pop), documentales de los museos más importantes del mundo y festivales europeos de verano."
  },
  {
    question: "¿Es rentable para un cine comercial o un auditorio municipal?",
    answer: "Absolutamente. Los eventos distribuidos por Versión Digital se sitúan sistemáticamente en el Top 3 de rendimiento por copia. Permiten optimizar horarios de baja afluencia y atraer a un público con mayor poder adquisitivo y fidelidad, generando ingresos adicionales en taquilla y consumo de bar."
  },
  {
    question: "¿Cómo puedo adquirir el dispositivo Bravo?",
    answer: "Bravo se ofrece en un modelo de cesión de uso por depósito. El programador realiza un depósito de garantía de 250 €, el cual es totalmente reembolsable al finalizar el contrato y devolver el equipo. No hay cuotas de compra iniciales elevadas, lo que minimiza el riesgo de inversión para la sala."
  },
  {
    question: "¿Qué modelos de contratación existen para los contenidos?",
    answer: "Nos adaptamos a cada tipo de sala con dos modalidades:\n\n• Tanto Alzado (Flat Fee): Un precio fijo por pase, ideal para programaciones municipales con presupuestos cerrados.\n\n• Porcentaje de Taquilla (Revenue Share): Un reparto de los ingresos netos entre el cine y la distribuidora, ideal para cines comerciales que buscan compartir el riesgo y el éxito."
  },
  {
    question: "¿Qué sucede si hay una caída de internet durante un directo?",
    answer: "El dispositivo Bravo cuenta con un sistema de buffer avanzado y tecnología de reconexión automática. Además, en caso de incidencia técnica mayor ajena a la sala, nuestro equipo ofrece soporte para reprogramar el contenido en diferido (\"Encore\") sin costes adicionales, protegiendo así la reputación de su centro frente al público."
  },
  {
    question: "¿Versión Digital proporciona materiales de marketing para promocionar los eventos?",
    answer: "Sí. Al contratar cualquier contenido, CinemaStreaming pone a su disposición un kit de prensa completo que incluye: póster digital en alta resolución, tráilers oficiales para pantalla y redes sociales, materiales para programas de mano y notas de prensa. Todo lo necesario para asegurar el éxito de la convocatoria."
  }
];

const FAQItem = ({ question, answer, isOpen, onClick, index }) => {
  return (
    <div 
      className={`border-b border-white/10 transition-all duration-300 ${isOpen ? 'bg-white/5' : ''}`}
    >
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between py-6 px-6 text-left hover:bg-white/5 transition-colors group"
        data-testid={`faq-question-${index}`}
      >
        <div className="flex items-start gap-4 pr-4">
          <span className="text-[#e4002b] font-bold text-lg min-w-[32px]">{String(index + 1).padStart(2, '0')}</span>
          <span className={`text-lg font-medium transition-colors ${isOpen ? 'text-[#e4002b]' : 'text-white group-hover:text-white/90'}`}>
            {question}
          </span>
        </div>
        <div className={`flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${
          isOpen 
            ? 'border-[#e4002b] bg-[#e4002b]' 
            : 'border-white/30 group-hover:border-white/50'
        }`}>
          <ChevronDown 
            className={`w-5 h-5 transition-transform duration-300 ${
              isOpen ? 'rotate-180 text-white' : 'text-white/70'
            }`} 
          />
        </div>
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-6 pl-[72px]">
          <p className="text-white/70 leading-relaxed whitespace-pre-line">
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
};

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#e4002b]/10 via-transparent to-transparent" />
        
        <div className="max-w-5xl mx-auto px-4 relative z-10">
          {/* Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#e4002b]/10 border border-[#e4002b]/30 rounded-full">
              <HelpCircle className="w-4 h-4 text-[#e4002b]" />
              <span className="text-[#e4002b] text-sm font-medium">Centro de Ayuda</span>
            </div>
          </div>
          
          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white text-center mb-6">
            Preguntas <span className="text-[#e4002b]">Frecuentes</span>
          </h1>
          
          <p className="text-white/60 text-lg md:text-xl text-center max-w-3xl mx-auto">
            Resolvemos todas tus dudas sobre CinemaStreaming y el dispositivo Bravo. 
            Encuentra respuestas rápidas a las preguntas más comunes de nuestros programadores.
          </p>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-12 pb-24">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-[#141414] rounded-2xl overflow-hidden border border-white/10">
            {faqData.map((item, index) => (
              <FAQItem
                key={index}
                index={index}
                question={item.question}
                answer={item.answer}
                isOpen={openIndex === index}
                onClick={() => handleToggle(index)}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* Contact CTA Section */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-gradient-to-r from-[#e4002b]/20 via-[#e4002b]/10 to-[#e4002b]/20 rounded-2xl p-8 md:p-12 border border-[#e4002b]/30">
            <MessageCircle className="w-12 h-12 text-[#e4002b] mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              ¿No encuentras lo que buscas?
            </h2>
            <p className="text-white/60 mb-8 max-w-xl mx-auto">
              Nuestro equipo está disponible para resolver cualquier duda adicional sobre 
              el servicio, condiciones o aspectos técnicos.
            </p>
            <a
              href="mailto:info@versiondigital.es?subject=Consulta desde FAQ"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#e4002b] text-white font-semibold rounded-xl hover:bg-[#c70024] transition-colors"
              data-testid="contact-from-faq"
            >
              <Mail className="w-5 h-5" />
              Contactar con nosotros
            </a>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
